import {cloneDeep, merge, pickBy } from "lodash";

export const initialKioskSettings = {
    abbreviatedTZ: '',
    error: '',
    // Both error and informationalMsg are displayed in same place as a banner.
    // However, informationalMsg does not disable the save button, but error does.
    informationalMsg: '',
    timezone: '',
    virtualCheckInEnabled: false,
    virtualCheckInScheduleEnabled: false,
    virtualCheckInHoursSchedule: []
};

const withoutErrors = (object) => pickBy(object, (_, key) => key.toLowerCase().indexOf('error') === -1)

// Returns a copy of the schedule without validation errors. Validation errors are properties that contain error in the name.
// Validation error example: Empty open time,Empty close time
export const withoutScheduleErrors = (virtualCheckInHoursSchedule) =>
    virtualCheckInHoursSchedule.map(day => ({...withoutErrors(day), timeSlots: day.timeSlots.map(withoutErrors)}))

// Callbacks required to update days and timeslots within a kiosk schedule
export const slotStateChangeCallbacks = (dispatch) => {
    return {
        addDayRow: () => dispatch({ type: 'ADD_DAY' }),
        clearEmptyRows: () => dispatch({ type: 'CLEAR_EMPTY_ROWS' }),
        copyDayRow: (rowIndex) => dispatch({ type: 'COPY_DAY', rowIndex: rowIndex }),
        removeAllRows: () => dispatch({ type: 'REMOVE_ALL_ROWS' }),
        removeDayRow: (rowIndex) => dispatch({ type: 'REMOVE_DAY', rowIndex: rowIndex }),
        validate: () => dispatch({ type: 'VALIDATE', submit: true }),
        timeSlotCallBacks: {
            addTimeSlot: (rowIndex) =>
                dispatch({type: 'ADD_SLOT', rowIndex: rowIndex}),
            removeTimeSlot: (rowIndex, slotIndex) =>
                dispatch({type: 'REMOVE_SLOT', rowIndex: rowIndex, slotIndex: slotIndex}),
            setDayOfWeek: (rowIndex, dayOfWeek) =>
                dispatch({type: 'SET_DAY_OF_WEEK', rowIndex: rowIndex, dayOfWeek: dayOfWeek}),
            setOpenTime: (rowId, slotId, openTime) =>
                dispatch({type: 'SET_OPEN_TIME', rowIndex: rowId, slotId: slotId, openTime: openTime}),
            setCloseTime: (rowId, slotId, closeTime) =>
                dispatch({type: 'SET_CLOSE_TIME', rowIndex: rowId, slotId: slotId, closeTime: closeTime})
        }
    }
}

// Checks if the day of week is present in a day schedule and sets an error if it's missing.
// If [clearExistingError] is true then the error will be cleared if new data is valid and no new error is set.
// Use clearExistingError=true for incremental form validation, clearExistingError=false for form submit validation.
export const validateDayOfWeekPresent = (day, clearExistingErrorOnly) =>
    day.error = day.dayOfWeek === "" ? (clearExistingErrorOnly ? day.error : "Empty day") : "";

// Checks if the open time is present in a day schedule and sets an error if it's missing. Behaves like validateDayOfWeekPresent.
export const validateOpenTimePresent = (day, clearExistingErrorOnly) =>
    day.timeSlots.forEach(slot => slot.openTimeError = slot.openTime ? "" : clearExistingErrorOnly ? slot.openTimeError : "Empty open time" )

// Checks if the close time is present in a day schedule and sets an error if it's missing. Behaves like validateDayOfWeekPresent.
export const validateCloseTimePresent = (day, clearExistingErrorOnly) =>
    day.timeSlots.forEach(slot => slot.closeTimeError = slot.closeTime ? "" : clearExistingErrorOnly ? slot.closeTimeError : "Empty close time" )

const isScheduleDayEmpty = (day) =>
    day.dayOfWeek === "" && !day.timeSlots.some(slot => slot.closeTime !== "" && slot.openTime !== "")

const isSlotOverlapped = (firstSlot, secondSlot) =>
    !(firstSlot.openTime >= secondSlot.closeTime || firstSlot.closeTime <= secondSlot.openTime)

const isSlotMissingAnyTimes = (slot) => slot.openTime === "" || slot.closeTime === ""

/**
 * Runs validation against all timeslots for a schedule day, marking overlapping times
 * within the same day as errors. This will only check for slots that have both an openTime and closeTime set, and
 * clear any errors that no longer
 * remain.
 * @param {SFVirtualCheckInDaySchedule} scheduleDay - the day object being validated
 * @returns {SFVirtualCheckInDaySchedule} the updated day object with updated error fields
 */
export const validateCheckSlotOverlap = (scheduleDay) =>
    scheduleDay.timeSlots.forEach(primarySlot => {
        // If the slot doesn't have both times or is the only slot, it can't be overlapping
        if (scheduleDay.timeSlots.length < 2 || isSlotMissingAnyTimes(primarySlot)) {
            primarySlot.overlapError = "";
            return;
        }
        // Check if any other non-empty slots have any conflicts with the primary slot
        primarySlot.overlapError = (scheduleDay.timeSlots
                .filter(comparisonSlot => !isSlotMissingAnyTimes(comparisonSlot) && primarySlot !== comparisonSlot)
                .some(comparisonSlot => isSlotOverlapped(primarySlot, comparisonSlot))
        ) ? "Overlaps with another slot" : ""
    });

/**
 * Checks if the state's virtualCheckInHoursSchedule has errors set on any items in the schedule.
 * @param {SFVirtualCheckInDaySchedule[]} scheduleDays - the array of schedule days for the kiosk
 * @returns {boolean} whether the schedule contains errors for any items within it
 */
export const invalidSchedule = (scheduleDays) =>
    scheduleDays.length > 0 && scheduleDays.some(
        schedule => schedule.error || schedule.timeSlots.some(
            timeSlots => timeSlots.openTimeError || timeSlots.closeTimeError || timeSlots.overlapError
        )
    );

export const allDaysOfWeekSelected = (virtualCheckInHoursSchedule) => virtualCheckInHoursSchedule.length >= 7;

export function kioskSettingsReducer(state, action) {

    function resetErrors(newState) {
        return kioskSettingsReducer(newState, {type: 'VALIDATE'})
    }

    let newState = cloneDeep(state);
    switch(action.type) {
        case 'LOAD_SETTINGS':
            return merge(newState, action.elements);
        case 'ERROR':
            return {
                ...newState,
                error: action.elements
            }
        case 'VALIDATE':
            newState.virtualCheckInHoursSchedule.forEach(day => {
                validateDayOfWeekPresent(day, !action.submit);
                validateOpenTimePresent(day, !action.submit);
                validateCloseTimePresent(day, !action.submit);
                validateCheckSlotOverlap(day);
            });

            return {
                ...newState,
                informationalMsg: '',
                error: invalidSchedule(newState.virtualCheckInHoursSchedule) ? "Please address errors in Virtual Check-in Open Hours schedule." : ""
            }
        case 'SET_DAY_OF_WEEK':
            newState.virtualCheckInHoursSchedule[action.rowIndex].dayOfWeek = action.dayOfWeek;
            return resetErrors(newState);
        case 'SET_OPEN_TIME':
            newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots[action.slotId].openTime = action.openTime;
            return resetErrors(newState);
        case 'SET_CLOSE_TIME':
            newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots[action.slotId].closeTime = action.closeTime;
            return resetErrors(newState);
        case 'ADD_SLOT':
            if(newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots.length >= 5 ) {
                return {
                    ...newState,
                    informationalMsg: "A maximum of 5 time slots can be entered for each day."
                }
            }
            const newSlot = {
                openTime: "",
                closeTime: "",
                openTimeError: "",
                closeTimeError: ""
            };
            newState.informationalMsg = '';
            newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots.push(newSlot);
            return newState;
        case 'REMOVE_SLOT':
            newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots.splice(action.slotIndex, 1)
            return resetErrors(newState);
        case 'ADD_DAY':
            if(allDaysOfWeekSelected(state.virtualCheckInHoursSchedule))
                return {
                    ...newState,
                    informationalMsg: "There is already a row for each day of the week."
                }
            const item = {
                dayOfWeek: "",
                error: "",
                timeSlots: [{
                    openTime: "",
                    closeTime: "",
                    openTimeError: "",
                    closeTimeError: ""
                }]
            };
            newState.virtualCheckInHoursSchedule.push(item)
            return newState;
        case 'COPY_DAY':
            if(allDaysOfWeekSelected(state.virtualCheckInHoursSchedule))
                return {
                    ...newState,
                    informationalMsg: "There is already a row for each day of the week."
                }
            newState.virtualCheckInHoursSchedule.push({
                dayOfWeek: "",
                error: "",
                timeSlots: cloneDeep(newState.virtualCheckInHoursSchedule[action.rowIndex].timeSlots)
            })
            return newState;
        case 'CLEAR_EMPTY_ROWS':
            return Object.assign({}, state, {
                virtualCheckInHoursSchedule: state.virtualCheckInHoursSchedule.filter(scheduleDay => !isScheduleDayEmpty(scheduleDay))
            });
        case 'REMOVE_ALL_ROWS':
            return Object.assign({}, state, {error: "", informationalMsg: "", virtualCheckInHoursSchedule: []});
        case 'REMOVE_DAY':
            newState.virtualCheckInHoursSchedule.splice(action.rowIndex, 1)
            return resetErrors(newState);
        case 'SET_VALUE':
            // Do not disable the schedule display if there are errors in it.
            if (action.elements.virtualCheckInScheduleEnabled === false
                && kioskSettingsReducer(newState, {type: 'VALIDATE'}).error) return state;
            return merge({}, state, action.elements);
    }
}
